import { savedArtifactCanvasTarget, type ArtifactCanvasTarget } from './routes.ts';

const text = (value: unknown): value is string => typeof value === 'string' && value.length > 0;

/**
 * The saved Artifact a normal Chat `save_artifact` call really committed, or null.
 *
 * The destination comes only from the committed receipt's own tuple: status `committed`, the
 * artifact/version/revision ids and the version number the writer returned (its kind, when present,
 * is a plain string; the viewer gates on the loaded Artifact's own kind). A refusal, an unknown
 * outcome (`unknown`, `unresolved`), an unfinished call or a tuple that does not agree with its own `actionTarget` opens nothing
 * and refreshes nothing; nothing is guessed from the title, the answer or the URL.
 */
export function committedSaveTarget(output: unknown): ArtifactCanvasTarget | null {
  if (!output || typeof output !== 'object') return null;
  const receipt = output as Record<string, unknown>;
  if (
    receipt.status !== 'committed' ||
    (receipt.kind !== undefined && !text(receipt.kind)) ||
    !text(receipt.artifactId) ||
    !text(receipt.versionId) ||
    !text(receipt.revisionId) ||
    typeof receipt.versionNumber !== 'number' ||
    receipt.purpose !== 'editor'
  )
    return null;
  const target = savedArtifactCanvasTarget({
    artifactId: receipt.artifactId,
    versionId: receipt.versionId,
    revisionId: receipt.revisionId,
    versionNumber: receipt.versionNumber,
    purpose: 'editor',
  });
  if (!target) return null;
  // The writer's own complete target is required and must agree with the tuple it reports.
  const named = receipt.actionTarget as Record<string, unknown> | undefined;
  if (
    !named ||
    typeof named !== 'object' ||
    named.kind !== 'artifact' ||
    named.id !== target.id ||
    named.versionId !== target.versionId ||
    named.revisionId !== target.revisionId ||
    named.versionNumber !== target.versionNumber ||
    named.purpose !== target.purpose
  )
    return null;
  return target;
}
